import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  CircularProgress,
  Slide,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Star as StarIcon,
  Check as CheckIcon,
  AttachMoney as MoneyIcon,
  Percent as PercentIcon,
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useEmpresa } from '../context/EmpresaContext';
import config from '../config';

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const GestionTrabajadoresDialog = ({
  open,
  onClose,
  ordenId,
  ordenEstado,
  trabajadoresAsignados = [],
  serviciosActuales = [],
  onTrabajadoresUpdated
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [trabajadoresDisponibles, setTrabajadoresDisponibles] = useState([]);
  const [trabajadoresActuales, setTrabajadoresActuales] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);

  const [nuevoTrabajador, setNuevoTrabajador] = useState({
    id_trabajador: '',
    precio_servicio: 0,
    comision: 10,
    es_responsable: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();

  const puedeModificar = [1, 2].includes(ordenEstado);
  const moneda = simboloMoneda || 'S/';

  const precioTotal = serviciosActuales?.reduce((t, s) => t + (s.cantidad * s.precio), 0) || 0;

  useEffect(() => {
    if (open) {
      setTrabajadoresActuales([...trabajadoresAsignados]);
      fetchTrabajadoresDisponibles();
      setError('');
      setSuccess('');
      setEditingId(null);
      setShowAddForm(false);
    }
  }, [open, trabajadoresAsignados]);

  useEffect(() => {
    if (open) {
      const numTrabajadores = trabajadoresActuales.length + 1;
      const precioSugerido = Math.round((precioTotal / numTrabajadores) * 100) / 100;
      setNuevoTrabajador(prev => ({ ...prev, precio_servicio: precioSugerido }));
    }
  }, [open, trabajadoresActuales.length, serviciosActuales]);

  const fetchTrabajadoresDisponibles = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/trabajadores`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setTrabajadoresDisponibles(response.data);
    } catch (err) {
      setError('Error al cargar trabajadores');
    }
  };

  const agregarTrabajador = async () => {
    if (!nuevoTrabajador.id_trabajador || !nuevoTrabajador.precio_servicio) {
      setError('Seleccione un trabajador y especifique el precio');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/api/ordenes/${ordenId}/asignar-trabajador`,
        {
          idTrabajador: nuevoTrabajador.id_trabajador,
          precioServicio: nuevoTrabajador.precio_servicio,
          comision: nuevoTrabajador.comision,
          idEstadoServicio: 1,
          esResponsable: nuevoTrabajador.es_responsable || trabajadoresActuales.length === 0
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      const sel = trabajadoresDisponibles.find(t => t.id_trabajador === parseInt(nuevoTrabajador.id_trabajador));
      setTrabajadoresActuales(prev => [...prev, {
        ...response.data,
        nombre_trabajador: sel.nombre_trabajador,
        apellido_trabajador: sel.apellido_trabajador,
        telefono_trabajador: sel.telefono_trabajador,
        email_trabajador: sel.email_trabajador,
        descripcion_estado: 'Asignado',
        es_responsable: nuevoTrabajador.es_responsable || trabajadoresActuales.length === 0
      }]);

      setNuevoTrabajador({ id_trabajador: '', precio_servicio: 0, comision: 10, es_responsable: false });
      setShowAddForm(false);
      setSuccess('Trabajador agregado');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al asignar');
    } finally {
      setLoading(false);
    }
  };

  const eliminarTrabajador = async (idAsignacion, nombre) => {
    if (!window.confirm(`¿Eliminar a ${nombre}?`)) return;
    setLoading(true);
    try {
      await axios.delete(
        `${config.API_BASE_URL}/api/asignacion-trabajadores/${idAsignacion}/desasignar-trabajador`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setTrabajadoresActuales(prev => prev.filter(t => t.id_asignacion !== idAsignacion));
      setSuccess('Trabajador eliminado');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const guardarEdicion = async (trabajador) => {
    setLoading(true);
    try {
      await axios.put(
        `${config.API_BASE_URL}/api/asignacion-trabajadores/${trabajador.id_asignacion}/editar-trabajador`,
        {
          idTrabajador: trabajador.id_trabajador,
          precioServicio: editData.precio_servicio,
          comision: editData.comision,
          idEstadoServicio: trabajador.id_estado_servicio
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setTrabajadoresActuales(prev => prev.map(t =>
        t.id_asignacion === trabajador.id_asignacion ? { ...t, ...editData } : t
      ));
      setEditingId(null);
      setSuccess('Actualizado');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar');
    } finally {
      setLoading(false);
    }
  };

  const cambiarResponsable = async (idAsignacion) => {
    setLoading(true);
    try {
      await axios.put(
        `${config.API_BASE_URL}/api/asignacion-trabajadores/${idAsignacion}/set-responsable`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setTrabajadoresActuales(prev => prev.map(t => ({
        ...t, es_responsable: t.id_asignacion === idAsignacion
      })));
      setSuccess('Responsable actualizado');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const intercambiarTrabajador = async (original, nuevoId) => {
    setLoading(true);
    try {
      await axios.delete(
        `${config.API_BASE_URL}/api/asignacion-trabajadores/${original.id_asignacion}/desasignar-trabajador`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      const response = await axios.post(
        `${config.API_BASE_URL}/api/ordenes/${ordenId}/asignar-trabajador`,
        {
          idTrabajador: nuevoId,
          precioServicio: original.precio_servicio,
          comision: original.comision,
          idEstadoServicio: 1,
          esResponsable: original.es_responsable
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      const nuevoData = trabajadoresDisponibles.find(t => t.id_trabajador === parseInt(nuevoId));
      setTrabajadoresActuales(prev => prev.map(t =>
        t.id_asignacion === original.id_asignacion ? {
          ...response.data,
          nombre_trabajador: nuevoData.nombre_trabajador,
          apellido_trabajador: nuevoData.apellido_trabajador,
          telefono_trabajador: nuevoData.telefono_trabajador,
          email_trabajador: nuevoData.email_trabajador,
          descripcion_estado: 'Asignado',
          es_responsable: original.es_responsable
        } : t
      ));
      setSuccess('Trabajador intercambiado');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al intercambiar');
    } finally {
      setLoading(false);
    }
  };

  const guardarCambios = () => {
    onTrabajadoresUpdated && onTrabajadoresUpdated();
    onClose();
  };

  const disponiblesParaAgregar = trabajadoresDisponibles
    .filter(t => !trabajadoresActuales.find(ta => ta.id_trabajador === t.id_trabajador));

  const comisionGana = (precio, comision, comisionFlat) => {
    if (comisionFlat > 0) return comisionFlat;
    return (precio * comision / 100);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      TransitionComponent={isMobile ? Transition : undefined}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100%' : '90vh',
        }
      }}
    >
      {/* ===== HEADER ===== */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
        color: '#fff',
        px: 2.5,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <PersonAddIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
            Equipo de Trabajo
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
            {trabajadoresActuales.length} asignado{trabajadoresActuales.length !== 1 ? 's' : ''}
            {precioTotal > 0 && ` · ${moneda} ${precioTotal.toFixed(2)} total`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mx: 2, mt: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        {!puedeModificar && (
          <Alert severity="warning" sx={{ mx: 2, mt: 2, borderRadius: 2 }}>
            Solo se pueden modificar en estado Pendiente o Asignado
          </Alert>
        )}

        {/* ===== LISTA DE TRABAJADORES ===== */}
        <Box sx={{ p: 2 }}>
          {trabajadoresActuales.length === 0 ? (
            <Box sx={{
              textAlign: 'center', py: 4, px: 2,
              bgcolor: '#F8FAFC', borderRadius: 3, border: '2px dashed #CBD5E1',
            }}>
              <PersonAddIcon sx={{ fontSize: 48, color: '#94A3B8', mb: 1 }} />
              <Typography sx={{ color: '#64748B', fontSize: 14, fontWeight: 600 }}>
                Sin trabajadores asignados
              </Typography>
              <Typography sx={{ color: '#94A3B8', fontSize: 12 }}>
                Agrega el primer miembro del equipo
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {trabajadoresActuales.map((t) => {
                const isEditing = editingId === t.id_asignacion;
                const initials = `${(t.nombre_trabajador || '')[0] || ''}${(t.apellido_trabajador || '')[0] || ''}`;
                const gana = comisionGana(t.precio_servicio, t.comision, t.comision_flat);

                return (
                  <Box key={t.id_asignacion} sx={{
                    border: '1px solid',
                    borderColor: t.es_responsable ? '#FFC107' : '#E2E8F0',
                    borderRadius: 3,
                    bgcolor: t.es_responsable ? '#FFFDF0' : '#fff',
                    overflow: 'hidden',
                  }}>
                    {/* Worker header row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5 }}>
                      <Avatar sx={{
                        width: 40, height: 40,
                        bgcolor: t.es_responsable ? '#FFC107' : '#1A237E',
                        color: t.es_responsable ? '#795548' : '#fff',
                        fontSize: 14, fontWeight: 800,
                      }}>
                        {t.es_responsable ? <StarIcon sx={{ fontSize: 20 }} /> : initials}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }} noWrap>
                          {t.nombre_trabajador} {t.apellido_trabajador}
                        </Typography>
                        {t.es_responsable && (
                          <Chip label="Responsable" size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#FFF3E0', color: '#E65100', mt: 0.3 }} />
                        )}
                      </Box>

                      {puedeModificar && !isEditing && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {!t.es_responsable && (
                            <Tooltip title="Hacer responsable">
                              <IconButton size="small" onClick={() => cambiarResponsable(t.id_asignacion)}
                                sx={{ color: '#FFC107', width: 30, height: 30 }}>
                                <StarIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => {
                              setEditingId(t.id_asignacion);
                              setEditData({ precio_servicio: t.precio_servicio, comision: t.comision });
                            }} sx={{ color: '#1A237E', width: 30, height: 30 }}>
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small"
                              onClick={() => eliminarTrabajador(t.id_asignacion, `${t.nombre_trabajador} ${t.apellido_trabajador}`)}
                              sx={{ color: '#EF4444', width: 30, height: 30 }}>
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>

                    {/* Price/Commission row */}
                    {!isEditing ? (
                      <Box sx={{
                        display: 'flex', borderTop: '1px solid #F1F5F9',
                        '& > div': { flex: 1, textAlign: 'center', py: 0.8 },
                      }}>
                        <Box>
                          <Typography sx={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>PRECIO</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>
                            {moneda} {parseFloat(t.precio_servicio || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ borderLeft: '1px solid #F1F5F9', borderRight: '1px solid #F1F5F9' }}>
                          <Typography sx={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>COMISION</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>
                            {t.comision_flat > 0 ? `${moneda} ${parseFloat(t.comision_flat).toFixed(2)}` : `${t.comision}%`}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>GANA</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#10B981' }}>
                            {moneda} {gana.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      /* Edit mode */
                      <Box sx={{ display: 'flex', gap: 1, p: 1.5, pt: 0, alignItems: 'center' }}>
                        <TextField
                          size="small" label="Precio" type="number"
                          value={editData.precio_servicio}
                          onChange={(e) => setEditData(prev => ({ ...prev, precio_servicio: parseFloat(e.target.value) || 0 }))}
                          InputProps={{ startAdornment: <InputAdornment position="start">{moneda}</InputAdornment> }}
                          sx={{ flex: 1, '& .MuiInputBase-root': { borderRadius: 2 } }}
                        />
                        <TextField
                          size="small" label="Comision %" type="number"
                          value={editData.comision}
                          onChange={(e) => setEditData(prev => ({ ...prev, comision: parseFloat(e.target.value) || 0 }))}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          sx={{ flex: 1, '& .MuiInputBase-root': { borderRadius: 2 } }}
                        />
                        <IconButton size="small" onClick={() => guardarEdicion(t)}
                          sx={{ bgcolor: '#10B981', color: '#fff', width: 34, height: 34, '&:hover': { bgcolor: '#059669' } }}>
                          <CheckIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => setEditingId(null)}
                          sx={{ bgcolor: '#EF4444', color: '#fff', width: 34, height: 34, '&:hover': { bgcolor: '#DC2626' } }}>
                          <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    )}

                    {/* Swap worker row */}
                    {puedeModificar && !isEditing && (
                      <Box sx={{ borderTop: '1px solid #F1F5F9', px: 1.5, py: 0.8 }}>
                        <FormControl fullWidth size="small">
                          <Select
                            value="" displayEmpty
                            onChange={(e) => intercambiarTrabajador(t, e.target.value)}
                            sx={{ fontSize: 12, borderRadius: 2, '& .MuiSelect-select': { py: 0.8 } }}
                            startAdornment={<SwapIcon sx={{ fontSize: 16, color: '#94A3B8', mr: 0.5 }} />}
                          >
                            <MenuItem value="" disabled>
                              <em style={{ fontSize: 12 }}>Reemplazar por...</em>
                            </MenuItem>
                            {trabajadoresDisponibles
                              .filter(d => d.id_trabajador !== t.id_trabajador)
                              .map(d => (
                                <MenuItem key={d.id_trabajador} value={d.id_trabajador} sx={{ fontSize: 13 }}>
                                  {d.nombre_trabajador} {d.apellido_trabajador}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* ===== ADD FORM ===== */}
          {puedeModificar && (
            <>
              {!showAddForm ? (
                <Box
                  onClick={() => setShowAddForm(true)}
                  sx={{
                    mt: 2, p: 1.5, border: '2px dashed #CBD5E1', borderRadius: 3,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                    cursor: 'pointer', color: '#64748B',
                    '&:hover': { borderColor: '#1A237E', color: '#1A237E', bgcolor: '#F8FAFC' },
                    transition: 'all 0.2s',
                  }}
                >
                  <PersonAddIcon sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Agregar trabajador</Typography>
                </Box>
              ) : (
                <Box sx={{
                  mt: 2, p: 2, borderRadius: 3,
                  bgcolor: '#F0F4FF', border: '1px solid #C7D2FE',
                }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1A237E', mb: 1.5 }}>
                    Nuevo miembro del equipo
                  </Typography>

                  {/* Worker select */}
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel>Trabajador</InputLabel>
                    <Select
                      value={nuevoTrabajador.id_trabajador}
                      label="Trabajador"
                      onChange={(e) => setNuevoTrabajador(prev => ({ ...prev, id_trabajador: e.target.value }))}
                      sx={{ borderRadius: 2, bgcolor: '#fff' }}
                    >
                      {disponiblesParaAgregar.map(t => (
                        <MenuItem key={t.id_trabajador} value={t.id_trabajador}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: '#1A237E' }}>
                              {(t.nombre_trabajador || '')[0]}
                            </Avatar>
                            <Typography sx={{ fontSize: 13 }}>
                              {t.nombre_trabajador} {t.apellido_trabajador}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Price + Commission row */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <TextField
                      size="small" label="Precio" type="number" fullWidth
                      value={nuevoTrabajador.precio_servicio}
                      onChange={(e) => setNuevoTrabajador(prev => ({ ...prev, precio_servicio: parseFloat(e.target.value) || 0 }))}
                      InputProps={{ startAdornment: <InputAdornment position="start">{moneda}</InputAdornment> }}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2, bgcolor: '#fff' } }}
                    />
                    <TextField
                      size="small" label="Comision" type="number" fullWidth
                      value={nuevoTrabajador.comision}
                      onChange={(e) => setNuevoTrabajador(prev => ({ ...prev, comision: parseFloat(e.target.value) || 0 }))}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      sx={{ '& .MuiInputBase-root': { borderRadius: 2, bgcolor: '#fff' } }}
                    />
                  </Box>

                  {/* Price hint */}
                  {precioTotal > 0 && (
                    <Typography sx={{ fontSize: 11, color: '#64748B', mb: 1.5 }}>
                      Total orden: {moneda} {precioTotal.toFixed(2)} · Sugerido por persona: {moneda} {(precioTotal / (trabajadoresActuales.length + 1)).toFixed(2)}
                    </Typography>
                  )}

                  {/* Preview: what worker earns */}
                  {nuevoTrabajador.precio_servicio > 0 && (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      p: 1, borderRadius: 2, bgcolor: '#ECFDF5', mb: 1.5,
                    }}>
                      <MoneyIcon sx={{ fontSize: 16, color: '#10B981' }} />
                      <Typography sx={{ fontSize: 12, color: '#065F46', fontWeight: 600 }}>
                        Gana: {moneda} {(nuevoTrabajador.precio_servicio * nuevoTrabajador.comision / 100).toFixed(2)}
                      </Typography>
                    </Box>
                  )}

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box
                      onClick={() => setShowAddForm(false)}
                      sx={{
                        flex: 1, py: 1, borderRadius: 2,
                        border: '1px solid #CBD5E1', textAlign: 'center', cursor: 'pointer',
                        fontSize: 13, fontWeight: 600, color: '#64748B',
                        '&:hover': { bgcolor: '#F1F5F9' },
                      }}
                    >
                      Cancelar
                    </Box>
                    <Box
                      onClick={loading ? undefined : agregarTrabajador}
                      sx={{
                        flex: 1.5, py: 1, borderRadius: 2,
                        bgcolor: !nuevoTrabajador.id_trabajador || loading ? '#CBD5E1' : '#1A237E',
                        textAlign: 'center', cursor: !nuevoTrabajador.id_trabajador || loading ? 'default' : 'pointer',
                        fontSize: 13, fontWeight: 700, color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                        '&:hover': { bgcolor: !nuevoTrabajador.id_trabajador || loading ? '#CBD5E1' : '#283593' },
                      }}
                    >
                      {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PersonAddIcon sx={{ fontSize: 16 }} />}
                      Agregar
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      {/* ===== FOOTER ===== */}
      <Box sx={{
        borderTop: '1px solid #E2E8F0',
        p: 2, display: 'flex', gap: 1.5,
      }}>
        <Box
          onClick={onClose}
          sx={{
            flex: 1, py: 1.2, borderRadius: 2.5, textAlign: 'center',
            border: '1.5px solid #E2E8F0', cursor: 'pointer',
            fontSize: 14, fontWeight: 600, color: '#64748B',
            '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
          }}
        >
          Cerrar
        </Box>
        <Box
          onClick={guardarCambios}
          sx={{
            flex: 1.5, py: 1.2, borderRadius: 2.5, textAlign: 'center',
            background: 'linear-gradient(135deg, #10B981, #059669)',
            cursor: 'pointer',
            fontSize: 14, fontWeight: 700, color: '#fff',
            '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' },
          }}
        >
          Guardar y cerrar
        </Box>
      </Box>
    </Dialog>
  );
};

export default GestionTrabajadoresDialog;
